import { useCallback, useMemo } from 'react';

import { useFocusEffect } from 'expo-router';

import { LoyaltyCmsType, useGetLoyaltyQuestsQuery } from 'generated/graphql-gateway';
import logger from 'utils/logger';

import { createQuest, validateQuest } from './create-quest';
import { IQuest, IUseLoyaltyQuestQuery } from './types';

export const useLoyaltyQuestQuery = ({
  loyaltyId,
  cmsType,
  refecthOnFocus,
}: {
  loyaltyId: string;
  cmsType?: LoyaltyCmsType;
  refecthOnFocus?: boolean;
}): IUseLoyaltyQuestQuery => {
  const { loading, data, error, refetch } = useGetLoyaltyQuestsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      loyaltyId,
      where: {
        activeOrEligible: true,
      },
    },
    skip: !loyaltyId,
  });

  const loyaltyUserQuests = data?.loyaltyUserV2?.quests;
  const quest = useMemo(() => {
    if (!loyaltyUserQuests) {
      return null;
    }

    // create and validate quests
    // invalid quests should be filtered out
    const questList = loyaltyUserQuests.map(q => {
      const createdQuest = createQuest(q);
      const questErrors = validateQuest(createdQuest);

      if (questErrors.length) {
        logger.warn(`Quest validation ${JSON.stringify(questErrors)}`);
        return null;
      }

      return createdQuest;
    });

    // filter invalid quests
    return questList.find(
      (quest: IQuest | null): quest is IQuest =>
        quest !== null &&
        (cmsType
          ? quest.loyaltyCmsType === cmsType
          : quest.loyaltyCmsType !== LoyaltyCmsType.USER_JOURNEY)
    );
    // stringifying the response to create the quest list only if the response has changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(loyaltyUserQuests)]);

  useFocusEffect(
    useCallback(() => {
      if (refecthOnFocus) {
        refetch({ loyaltyId, where: { activeOrEligible: true } });
      }
    }, [refecthOnFocus, refetch, loyaltyId])
  );

  return {
    loadingQuests: loading,
    quest: quest || null,
    error,
    refetchQuests: refetch,
  };
};
